import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchLaneKpis } from '../../state/features/lanesSlice';
import { ApiStatus } from '../../utils/enums';
import { formatNumber } from '../../utils/formattingUtils';
import { useFilters } from '../Filters/useFilters';
import KPICard from './KPICard';
import KPIBanner from './KPIBanner';

const LaneKPIs = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const filters = useFilters();

    const kpiApiStatus = useAppSelector((state) => { return state.lanes.kpiApiStatus; });
    const kpis = useAppSelector((state) => { return state.lanes.kpis; });

    useEffect(() => {
        dispatch(fetchLaneKpis({ filter_criteria: filters }));
    }, [dispatch, filters]);

    const loading = kpiApiStatus === ApiStatus.Idle || kpiApiStatus === ApiStatus.Loading;

    const avgRatePerMile = kpis.total_revenue_usd_amount !== null && kpis.loaded_distance_miles_amount ?
        formatNumber(kpis.total_revenue_usd_amount / kpis.loaded_distance_miles_amount, 2, 2) :
        null;

    const lengthOfHaul = kpis.loaded_distance_miles_amount !== null && kpis.freight_order_count ?
        formatNumber(kpis.loaded_distance_miles_amount / kpis.freight_order_count, 0, 0) :
        null;

    const averageYield = kpis.average_network_order_yield_usd !== null ?
        formatNumber(kpis.average_network_order_yield_usd, 0, 0) :
        null;

    const avgEmptyMiles = kpis.empty_distance_miles_amount !== null && kpis.freight_order_count ?
        formatNumber(kpis.empty_distance_miles_amount / kpis.freight_order_count, 2, 2) :
        null;

    const emptyMilesPercent = kpis.empty_distance_miles_amount !== null && kpis.travel_distance_miles_amount ?
        formatNumber((kpis.empty_distance_miles_amount / kpis.travel_distance_miles_amount) * 100, 2, 2) :
        null;

    const milesPerHour = kpis.loaded_distance_miles_amount !== null && kpis.first_stop_departure_to_final_stop_arrival_hours_duration ?
        formatNumber(kpis.loaded_distance_miles_amount / kpis.first_stop_departure_to_final_stop_arrival_hours_duration, 2, 2) :
        null;

    const revenuePerHour = kpis.total_revenue_usd_amount !== null && kpis.first_stop_departure_to_final_stop_arrival_hours_duration ?
        formatNumber(kpis.total_revenue_usd_amount / kpis.first_stop_departure_to_final_stop_arrival_hours_duration, 2, 2) :
        null;

    const inTransitDelay = kpis.netwise_in_transit_delay_hours_duration !== null && kpis.freight_order_count ?
        formatNumber(kpis.netwise_in_transit_delay_hours_duration / kpis.freight_order_count, 2, 2) :
        null;

    const failedStatusMessage = kpiApiStatus === ApiStatus.Failure ? 'Unable to load KPIs' : null;

    return (
        <KPIBanner>
            <KPICard
                title='Total Orders'
                stat={formatNumber(kpis.freight_order_count)}
                loading={loading}
                error={
                    !loading && kpis.freight_order_count === null ?
                        'No orders are available for selected lanes' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='Total RPM'
                stat={avgRatePerMile}
                prefix='$'
                unit='/mi'
                loading={loading}
                error={
                    !loading && avgRatePerMile === null ?
                        'No miles were travelled on selected lanes' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='LOH'
                stat={lengthOfHaul}
                unit='mi'
                loading={loading}
                error={
                    !loading && lengthOfHaul === null ?
                        'Length of haul unavailable for selected lanes' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='Avg. Yield'
                stat={averageYield}
                prefix='$'
                loading={loading}
                error={
                    !loading && averageYield === null ?
                        'Yield is unavailable' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='Avg. Empty Miles'
                stat={avgEmptyMiles}
                unit='mi'
                loading={loading}
                error={
                    !loading && avgEmptyMiles === null ?
                        'Empty miles unavailable for selected lanes' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='Empty Mile %'
                stat={emptyMilesPercent}
                unit='%'
                loading={loading}
                error={
                    !loading && emptyMilesPercent === null ?
                        'Empty miles percentage unavailable for selected lanes' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='Avg. Miles per Hour'
                stat={milesPerHour}
                unit='/hr'
                loading={loading}
                error={
                    !loading && milesPerHour === null ?
                        'Miles per hour unavailable for selected lanes' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='RPH'
                stat={revenuePerHour}
                prefix='$'
                unit='/hr'
                loading={loading}
                error={
                    !loading && revenuePerHour === null ?
                        'No transit hours are available for selected lanes' :
                        failedStatusMessage
                }
            />
            <KPICard
                title='In Transit Delay'
                stat={inTransitDelay}
                unit='/hr'
                loading={loading}
                error={
                    !loading && inTransitDelay === null ?
                        'In Transit Delay unavailable for selected lanes' :
                        failedStatusMessage
                }
            />
        </KPIBanner>
    );
};

export default LaneKPIs;
